import {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
  ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import { Language } from '@penta/i18n-init';
import {
  formatLanguageToLocaleRegion,
  formatLocaleRegionToLanguage,
} from '../utils/localeUtils';

export interface LanguageContextType {
  handleChangeLanguage: (newLang: Language) => void;
  language: Language;
}

export const LanguageContext = createContext<LanguageContextType | undefined>(
  undefined,
);

export const useLanguageToggle = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguageToggle must be used within LanguageProvider');
  }
  return context;
};

interface LanguageProviderProps {
  children: ReactNode;
  locale: string;
}

export const LanguageProvider = ({
  children,
  locale,
}: LanguageProviderProps) => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();

  const validatedLocale = formatLanguageToLocaleRegion(locale);
  const [language, setLanguage] = useState<Language>(validatedLocale);

  useEffect(() => {
    setLanguage(i18n.language as Language);
  }, [i18n.language]);

  const handleChangeLanguage = useCallback(
    (newLang: Language) => {
      i18n.changeLanguage(newLang);

      // Extract short locale for the URL (e.g. "cs" from "cs-CZ")
      const shortLocale = formatLocaleRegionToLanguage(newLang);

      navigate({
        to: '.',
        params: (prev: Record<string, unknown>) => ({
          ...prev,
          locale: shortLocale,
        }),
        search: (prev: Record<string, unknown>) => prev,
        replace: true,
      });
    },
    [i18n, navigate],
  );

  const contextValue = useMemo(
    () => ({ handleChangeLanguage, language }),
    [handleChangeLanguage, language],
  );

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
};
